import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ListIcon, EventIcon, MapIcon, MyEventsIcon, SettingsIcon } from '../icons/NavIcons';
import { useTranslation } from 'react-i18next';

/**
 * Footer de navigation principal avec 5 boutons
 * 
 * Caractéristiques:
 * - Boutons: Liste, Événement, Carte (central), Mes événements, Paramètres
 * - Chaque bouton a une icône SVG et un libellé
 * - Bouton Carte surélevé avec fond bleu
 * - Indication visuelle pour le bouton actif
 * - Support multilangue
 * - Chaque bouton occupe exactement 20% de la largeur pour un alignement parfait
 * - Gestion améliorée pour la barre d'adresse dynamique iOS
 */
const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Définition des boutons de navigation
  const navItems = [
    {
      label: t('navigation.list'),
      icon: ListIcon,
      path: '/list',
      active: location.pathname === '/list',
      ariaLabel: t('aria.listEvents')
    },
    {
      label: t('navigation.myEvents'),
      icon: MyEventsIcon,
      path: '/my-events',
      active: location.pathname === '/my-events',
      ariaLabel: t('aria.myEvents')
    },
    {
      label: t('navigation.map'),
      icon: MapIcon,
      path: '/',
      active: location.pathname === '/' || location.pathname === '/map',
      isMainButton: true,
      ariaLabel: t('aria.interactiveMap')
    },
    {
      label: t('navigation.createEvent'),
      icon: EventIcon,
      path: '/create-event',
      active: location.pathname === '/create-event',
      ariaLabel: t('aria.createEvent')
    },
    {
      label: t('navigation.settings'),
      icon: SettingsIcon,
      path: '/settings',
      active: location.pathname === '/settings',
      ariaLabel: t('aria.settings')
    }
  ];

  return (
    <footer className="fixed bottom-0 left-0 right-0 z-[1000] bg-white border-t border-gray-200 shadow-lg pb-safe">
      <div className="flex w-full">
        {navItems.map((item, index) => (
          <NavButton
            key={index}
            label={item.label}
            Icon={item.icon}
            onClick={() => navigate(item.path)}
            active={item.active}
            isMainButton={item.isMainButton}
            ariaLabel={item.ariaLabel}
          />
        ))}
      </div>
    </footer>
  );
};

/**
 * Composant de bouton de navigation
 * 
 * @param {string} label - Libellé du bouton
 * @param {Component} Icon - Composant SVG à afficher
 * @param {function} onClick - Action au clic
 * @param {boolean} active - Indique si le bouton est actif
 * @param {boolean} isMainButton - Indique s'il s'agit du bouton principal (carte)
 * @param {string} ariaLabel - Description pour l'accessibilité
 */
const NavButton = ({ label, Icon, onClick, active, isMainButton, ariaLabel }) => {
  // Classes communes à tous les boutons - chaque bouton occupe exactement 20% de la largeur
  const baseClasses = "flex flex-col items-center justify-center w-1/5 transition-all duration-200";
  
  // Classes spécifiques selon le type de bouton et son état
  const buttonClasses = isMainButton
    ? `${baseClasses} relative`
    : `${baseClasses} py-1 ${active ? 'text-[#2894D8]' : 'text-gray-500'}`;
  
  // Rendu du bouton principal (carte)
  if (isMainButton) {
    return (
      <button 
        className={buttonClasses}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        <div className="bg-[#2894D8] hover:bg-[#136E9D] rounded-full p-3 mb-1 -mt-5 shadow-lg transform transition-transform hover:scale-105">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <span className={`text-xs ${active ? 'text-[#2894D8] font-medium' : 'text-gray-500'}`}>
          {label}
        </span>
      </button>
    );
  }
  
  // Rendu des boutons standards
  return (
    <button 
      className={buttonClasses}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <Icon className={`w-6 h-6 mb-1 ${active ? 'text-[#2894D8]' : 'text-gray-500'}`} />
      <span className="text-xs truncate w-full text-center px-1">{label}</span>
    </button>
  );
};

export default Footer;
